import { addDoc, collection, getDocs, limit, query, Timestamp, where } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { auth, db, storage } from '../../config/firebase.js';
import logger from '../../utils/logger';
import { contentDisposition, normaliseTitle, storageFileName } from '../../utils/resourceNaming';

export const MAX_FILE_SIZE = 10 * 1024 * 1024;
export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

/** @returns {string} an error message, or '' when the file is acceptable */
export const validateFile = (file) => {
  if (!file) return 'Choose a file.';
  if (file.size > MAX_FILE_SIZE) return 'File is too large. Maximum size is 10 MB.';
  if (!ALLOWED_FILE_TYPES.includes(file.type)) return 'Invalid file type. Upload PDF, PPT or Word.';
  return '';
};

/**
 * True when a resource with the same title (ignoring case and spacing)
 * already exists in PDFS/{grade}/{subject}.
 */
export const isDuplicateTitle = async (grade, subject, title) => {
  const colRef = collection(db, 'PDFS', grade, subject);
  const clean = normaliseTitle(title);
  const [exact, folded] = await Promise.all([
    getDocs(query(colRef, where('title', '==', clean), limit(1))),
    getDocs(query(colRef, where('titleLower', '==', clean.toLowerCase()), limit(1))),
  ]);
  return !exact.empty || !folded.empty;
};

/**
 * Uploads one resource (file or link) and creates its unverified record,
 * which places it in the moderation queue.
 *
 * If the file uploads but the record cannot be written, the file is removed
 * again and the error is re-thrown (BUG-01).
 *
 * @param {object} r
 * @param {string} r.grade      clean grade, e.g. "Grade10"
 * @param {string} r.subject    PascalCase subject, e.g. "Mathematics"
 * @param {string} r.title      display title (REQ-04)
 * @param {string} r.theme
 * @param {string[]} r.tags
 * @param {string} r.contributor
 * @param {File} [r.file]       document to upload, or
 * @param {string} [r.linkURL]  link resource
 * @param {string} [r.batchId]  DISC-02 batch the upload belongs to
 * @param {(percent: number) => void} [r.onProgress]
 * @returns {Promise<string>} path of the created record
 */
export async function uploadResource({ grade, subject, title, theme, tags, contributor, file, linkURL, batchId, onProgress }) {
  const user = auth.currentUser;
  const cleanTitle = normaliseTitle(title);
  const record = {
    userID: user?.email || 'Default',
    uploaderUid: user?.uid || null,
    subject,
    grade,
    title: cleanTitle,
    titleLower: cleanTitle.toLowerCase(),
    theme: (theme || '').trim(),
    tags: [...tags],
    contributor: (contributor || '').trim() || 'anonymous',
    modifiedAt: Timestamp.now(),
    verified: false,
    reported: false,
    rating: 0,
    ratingAmount: 0,
    reportAmount: 0,
    ...(batchId ? { batchId } : {}),
  };
  const colRef = collection(db, 'PDFS', grade, subject);

  if (!file) {
    const created = await addDoc(colRef, { ...record, file_url: linkURL, file_path: '' });
    return created.path;
  }

  const filePath = `${grade}/${subject}/${storageFileName({ grade, subject, title: cleanTitle, originalName: file.name })}`;
  const fileRef = ref(storage, filePath);
  await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(fileRef, file, {
      contentType: file.type,
      contentDisposition: contentDisposition(cleanTitle, file.name),
      // Lets the uploader remove their own file if saving the record fails
      customMetadata: { uploaderUid: user?.uid || '' },
    });
    task.on(
      'state_changed',
      (snap) => onProgress?.((snap.bytesTransferred / snap.totalBytes) * 100),
      reject,
      resolve
    );
  });

  try {
    const downloadURL = await getDownloadURL(fileRef);
    const created = await addDoc(colRef, {
      ...record,
      file_url: downloadURL,
      file_path: filePath,
      originalFileName: file.name,
    });
    return created.path;
  } catch (err) {
    logger.error('Saving the resource record failed; removing the uploaded file:', err);
    await deleteObject(fileRef).catch(() => {});
    throw err;
  }
}

/** Analytics event, matching the existing file_upload events. */
export const logUploadEvent = async ({ subject, title, contributor }) => {
  try {
    await addDoc(collection(db, 'analytics_data'), {
      userID: auth.currentUser?.email || 'Default',
      event: 'file_upload',
      subject,
      title,
      timestamp: Timestamp.now(),
      contributor: (contributor || '').trim() || 'anonymous',
    });
  } catch (err) {
    logger.error('Analytics log error:', err);
  }
};
